import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/db.ts';
import { useAuth } from '../lib/auth.tsx';
import { logger } from '../services/logger.ts';

interface FICADocument {
  id: string;
  document_type: 'id_copy' | 'proof_of_address' | 'proof_of_bank_details';
  file_url: string;
  file_name: string | null;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface FICAStatus {
  is_verified: boolean;
  verified_at: string | null;
}

interface FICADocumentModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const DOCUMENT_TYPES = {
  id_copy: {
    label: 'Copy of ID',
    description: 'Upload a clear copy of your government-issued ID (ID card, passport, or driver\'s license)',
    acceptedTypes: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
  },
  proof_of_address: {
    label: 'Proof of Address',
    description: 'Upload a recent utility bill, bank statement, or official document showing your residential address (not older than 3 months)',
    acceptedTypes: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
  },
  proof_of_bank_details: {
    label: 'Proof of Bank Details',
    description: 'Upload a bank statement or letter from your bank showing your account details',
    acceptedTypes: ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
  }
} as const;

export function FICADocumentModal({ onClose, onSuccess }: FICADocumentModalProps) {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<FICADocument[]>([]);
  const [ficaStatus, setFicaStatus] = useState<FICAStatus | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchDocuments();
      fetchFICAStatus();
    }
  }, [user]);

  const fetchDocuments = async () => {
    if (!user) return;
    
    try {
      const { data, error: fetchError } = await supabase
        .from('fica_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setDocuments(data || []);
    } catch (err) {
      logger.error('Error fetching FICA documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchFICAStatus = async () => {
    if (!user) return;
    
    try {
      const { data, error: fetchError } = await supabase
        .from('fica_verification')
        .select('is_verified, verified_at')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle instead of single to handle missing rows

      if (fetchError) {
        // If table doesn't exist (406 or other errors), treat as not verified
        if (fetchError.code === 'PGRST116' || fetchError.code === '42P01' || fetchError.status === 406) {
          setFicaStatus({ is_verified: false, verified_at: null });
          return;
        }
        throw fetchError;
      }
      
      setFicaStatus(data || { is_verified: false, verified_at: null });
    } catch (err) {
      logger.error('Error fetching FICA status:', err);
      // Default to not verified if there's an error
      setFicaStatus({ is_verified: false, verified_at: null });
    }
  };

  const uploadFile = async (file: File, documentType: keyof typeof DOCUMENT_TYPES) => {
    if (!user) return;

    try {
      setUploading(documentType);
      setError(null);

      // Validate file type
      const acceptedTypes = DOCUMENT_TYPES[documentType].acceptedTypes;
      if (!acceptedTypes.includes(file.type)) {
        throw new Error(`Invalid file type. Please upload a ${acceptedTypes.join(', ')} file`);
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Upload to Supabase Storage
      const fileExt = file.name.split('.').pop();
      const fileName = `fica-${user.id}-${documentType}-${Date.now()}.${fileExt}`;
      const filePath = fileName; // Store directly in bucket root, not in a subfolder

      const { error: uploadError } = await supabase.storage
        .from('fica-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        // If bucket doesn't exist, create it or use a fallback
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('fica-documents')
        .getPublicUrl(filePath);

      // Save document record
      const { error: insertError } = await supabase
        .from('fica_documents')
        .upsert({
          user_id: user.id,
          document_type: documentType,
          file_url: publicUrl,
          file_name: file.name,
          file_size: file.size,
          status: 'pending'
        }, {
          onConflict: 'user_id,document_type'
        });

      if (insertError) throw insertError;

      await fetchDocuments();
      await fetchFICAStatus();
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error uploading FICA document:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, documentType: keyof typeof DOCUMENT_TYPES) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file, documentType);
  };

  const getDocumentStatus = (documentType: keyof typeof DOCUMENT_TYPES) => {
    const doc = documents.find(d => d.document_type === documentType);
    return doc;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'pending':
        return 'Pending Review';
      default:
        return 'Not Submitted';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-auto shadow-xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full mx-auto shadow-xl flex flex-col max-h-[90vh] my-8">
        {/* Fixed Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">FICA Document Submission</h3>
            <p className="text-sm text-gray-600 mt-1">
              Submit your FICA documents to enable withdrawals
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {ficaStatus?.is_verified && (
            <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Your FICA documents have been verified. You can now withdraw funds.</span>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <div className="space-y-6">
          {(Object.keys(DOCUMENT_TYPES) as Array<keyof typeof DOCUMENT_TYPES>).map((docType) => {
            const doc = getDocumentStatus(docType);
            const docInfo = DOCUMENT_TYPES[docType];
            const isUploading = uploading === docType;

            return (
              <div
                key={docType}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <FileText className="w-6 h-6 text-wayja-teal" />
                      <h4 className="text-lg font-semibold text-gray-800">{docInfo.label}</h4>
                      {doc && (
                        <div className="flex items-center gap-2">
                          {getStatusIcon(doc.status)}
                          <span className={`text-sm font-medium ${
                            doc.status === 'approved' ? 'text-green-600' :
                            doc.status === 'rejected' ? 'text-red-600' :
                            'text-yellow-600'
                          }`}>
                            {getStatusText(doc.status)}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{docInfo.description}</p>
                    {doc?.rejection_reason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        <strong>Rejection reason:</strong> {doc.rejection_reason}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block">
                    <input
                      type="file"
                      accept={docInfo.acceptedTypes.join(',')}
                      onChange={(e) => handleFileChange(e, docType)}
                      disabled={isUploading || doc?.status === 'approved'}
                      className="hidden"
                      id={`file-input-${docType}`}
                    />
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => document.getElementById(`file-input-${docType}`)?.click()}
                        disabled={isUploading || doc?.status === 'approved'}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          doc?.status === 'approved'
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-wayja-teal text-white hover:bg-opacity-90'
                        }`}
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Uploading...</span>
                          </>
                        ) : doc ? (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Replace Document</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Upload Document</span>
                          </>
                        )}
                      </button>
                      {doc && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 underline"
                        >
                          View Current Document
                        </a>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
          </div>

          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Important Information:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All three documents must be approved before you can withdraw funds</li>
                  <li>Documents are reviewed by our support team, usually within 24-48 hours</li>
                  <li>If your documents are rejected, you'll receive a reason and can resubmit</li>
                  <li>For assistance, please contact support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

